import React from "react";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import {
  UserLoggedInNavigatorNavigationProp,
  UserLoggedInNavigatorScreenProps,
} from "@/src/TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import {
  createNativeStackNavigator,
  NativeStackNavigationProp,
} from "@react-navigation/native-stack";
import { CompositeNavigationProp, RouteProp } from "@react-navigation/native";
import { useNavigationHeaderOptions } from "@/src/common/navigationHeaderOptions";
import { graphql, useLazyLoadQuery } from "react-relay";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import { useSizeClass } from "@/src/common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import { WidgetCalendarEventCreateScreenQuery } from "./__generated__/WidgetCalendarEventCreateScreenQuery.graphql";
import { InnerCalendarTeamEventCreateScreenComponent } from "./CalendarTeamEventCreate";

type Props = {
  navigation: CompositeNavigationProp<
    NativeStackNavigationProp<ParamList, "WidgetCalendarEventCreateScreen">,
    UserLoggedInNavigatorNavigationProp<"WidgetCalendarEventCreate">
  >;
  route: RouteProp<ParamList, "WidgetCalendarEventCreateScreen">;
};

type ParamList = {
  WidgetCalendarEventCreateScreen: { widgetId: string; connections: string[] };
};

const Stack = createNativeStackNavigator<ParamList>();

function WidgetCalendarEventCreateScreenComponent(
  props: Props & { fetchKey: string }
) {
  const data = useLazyLoadQuery<WidgetCalendarEventCreateScreenQuery>(
    graphql`
      query WidgetCalendarEventCreateScreenQuery($widgetId: ID!) {
        widgetCalendarOverview(id: $widgetId) {
          calendars(includeExternal: true) {
            ...CalendarTeamEventCreateScreenComponent_storeCalendars
          }
        }
        store {
          timezone
        }
      }
    `,
    {
      widgetId: props.route.params.widgetId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.widgetCalendarOverview) {
    return null;
  }

  return (
    <InnerCalendarTeamEventCreateScreenComponent
      calendars={data.widgetCalendarOverview.calendars}
      timezone={data.store.timezone}
      onClose={() => props.navigation.goBack()}
      connections={props.route.params.connections}
    />
  );
}

function WidgetCalendarEventCreateScreen(props: Props) {
  return (
    <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
      {(fk) => (
        <WidgetCalendarEventCreateScreenComponent {...props} fetchKey={fk} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

export const WidgetCalendarEventCreate = withWebModal(
  (props: UserLoggedInNavigatorScreenProps<"WidgetCalendarEventCreate">) => {
    const navigationHeaderOptions = useNavigationHeaderOptions();
    const intl_tokens = useIntlTokens();
    const isCompact = useSizeClass().width === SizeClass.COMPACT;
    return (
      <Stack.Navigator initialRouteName="WidgetCalendarEventCreateScreen">
        <Stack.Screen
          name="WidgetCalendarEventCreateScreen"
          component={WidgetCalendarEventCreateScreen}
          initialParams={{
            widgetId: props.route.params.widgetId,
            connections: props.route.params.connections,
          }}
          options={navigationHeaderOptions({
            type: "CANCEL",
            onClose: () => props.navigation.goBack(),
            title: isCompact ? "" : intl_tokens.CalendarEvent,
            defaultContentStyle: true,
          })}
        />
      </Stack.Navigator>
    );
  }
);
